import { classes } from 'html-classes';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { ModalSaveMore } from '~/components/ModalSaveMore';
import MultibuyLevels from '~/components/MultibuyLevels';
import { ProductViewProps } from '~/pages/Product/Product';
import { sortMultibuyLevels } from '~/utils/multibuy';

import styles from './ProductMultibuyLevels.module.scss';

type ProductMultibuyLevelsProps = Pick<
  ProductViewProps,
  'isMultiBuy' | 'currentItem' | 'isLoading' | 'count'
> &
  Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>;

export const ProductMultibuyLevels = ({
  isMultiBuy,
  currentItem,
  isLoading,
  count,
  className,
  ...props
}: ProductMultibuyLevelsProps) => {
  const { t } = useTranslation();

  const [isShowSaveMoreModal, setIsShowSaveMoreModal] = useState(false);

  if (!isMultiBuy || !currentItem?.multibuyPriceLevels?.length) {
    return null;
  }

  const sortedLevels = sortMultibuyLevels(currentItem.multibuyPriceLevels);

  const shouldShowMore = sortedLevels.length > 3;

  const levelsShort = shouldShowMore ? sortedLevels.slice(0, 3) : sortedLevels;

  const handleShowSaveMoreModal = () => {
    setIsShowSaveMoreModal(true);
  };

  const handleCloseSaveMoreModal = () => {
    setIsShowSaveMoreModal(false);
  };

  if (isLoading) {
    return (
      <div {...props} className={classes([styles.container, className])}>
        <div className={styles.levels}>
          <Skeleton
            containerClassName={styles.skeleton}
            count={2}
            height={40}
          />
        </div>
      </div>
    );
  }

  const basePrice = currentItem?.discountPrice || currentItem?.price || 0;

  return (
    <>
      <div {...props} className={classes([styles.container, className])}>
        <div className={styles.saveMoreBadge}>
          {t('phrases:multibuyOfferSaveMore')}
        </div>
        <div className={styles.levels}>
          <MultibuyLevels
            levels={levelsShort}
            allLevels={sortedLevels}
            count={count}
            basePrice={basePrice}
          />
          {shouldShowMore && (
            <button
              className={styles.seeAllButton}
              onClick={handleShowSaveMoreModal}
            >
              {t('seeAll')}
            </button>
          )}
        </div>
      </div>
      <ModalSaveMore
        isShow={isShowSaveMoreModal}
        onBackdropDismiss={handleCloseSaveMoreModal}
      >
        <MultibuyLevels
          levels={sortedLevels}
          count={count}
          basePrice={basePrice}
        />
      </ModalSaveMore>
    </>
  );
};
